import { Injectable } from '@angular/core';
import { ConfirmDialogService } from '@examdojo/core/confirm-dialog';
import { SupabaseSelectQueryConfig } from '@examdojo/core/supabase';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { QuestionItemV2 } from '@examdojo/models/question';
import { QuestionStoreModel } from '@examdojo/question';
import { rethrowError } from '@examdojo/rxjs';
import { TableInsertModel, TableUpdateModel } from '@examdojo/supabase';
import { ToastService } from '@examdojo/toast';
import { sanitizeObject } from '@examdojo/util/sanitize-object';
import { EMPTY, finalize, map, Observable, switchMap, tap } from 'rxjs';
import { ReferenceQuestionHttpService } from './reference-question-http.service';
import {
  REFERENCE_QUESTION_CREATE_ALLOWED_KEYS,
  REFERENCE_QUESTION_UPDATE_ALLOWED_KEYS,
  ReferenceQuestionCreateModel,
  ReferenceQuestionHttpModel,
  ReferenceQuestionStoreModel,
  ReferenceQuestionUpdateModel,
  ReferenceQuestionViewStoreModel,
} from './reference-question.model';
import { ReferenceQuestionStore } from './reference-question.store';

@Injectable({ providedIn: 'root' })
export class ReferenceQuestionService {
  constructor(
    private readonly store: ReferenceQuestionStore,
    private readonly httpService: ReferenceQuestionHttpService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly toastService: ToastService,
  ) {}

  fetchPaginatedListView(
    selectQueryConfig: SupabaseSelectQueryConfig = {},
  ): Observable<{ data: ReferenceQuestionViewStoreModel[]; totalCount: number }> {
    return this.httpService.fetchPaginatedListView(selectQueryConfig).pipe(
      map((data) => ({
        data: data.data as ReferenceQuestionViewStoreModel[],
        totalCount: data.totalCount,
      })),
    );
  }

  fetch(id: ReferenceQuestionStoreModel['id']): Observable<ReferenceQuestionStoreModel> {
    this.store.setLoading(true);

    return this.httpService.fetch(id).pipe(
      map((data) => this.mapReferenceQuestionHttpModelToStoreModel(data)),
      tap((model) => this.store.upsertMany([model])),
      tap(() => {
        this.store.setActive(id);
      }),
      finalize(() => this.store.setLoading(false)),
      this.errorHandlerService.catchError(
        '[ReferenceQuestionService]: Fetching reference questions failed',
        rethrowError(),
        {
          toast: (err) => ({
            title: 'An error occurred while fetching the reference question',
            description: (err as Error)?.message,
          }),
          context: {
            referenceQuestionId: id,
          },
        },
      ),
    );
  }

  resetActiveReferenceQuestion() {
    this.store.setActive(null);
  }

  fetchByQuestionId(questionId: QuestionStoreModel['id']): Observable<ReferenceQuestionStoreModel | null> {
    return this.httpService.fetchByQuestionId(questionId).pipe(
      map((result) => (result ? this.mapReferenceQuestionHttpModelToStoreModel(result) : null)),
      tap((model) => model && this.store.upsertMany([model])),
      this.errorHandlerService.catchError(
        '[ReferenceQuestionService]: Fetching reference question by question ID failed',
        rethrowError(),
        {
          toast: (err) => ({
            title: 'An error occurred while fetching the reference question',
            description: (err as Error)?.message,
          }),
          context: {
            referenceQuestionId: questionId,
          },
        },
      ),
    );
  }

  fetchSyllabusIdByReferenceQuestionId(id: ReferenceQuestionStoreModel['id']): Observable<number | null> {
    return this.httpService.fetchSyllabusIdByReferenceQuestionId(id).pipe(
      tap((syllabusId) => {
        if (isNotNullish(syllabusId)) {
          this.store.updateProp('syllabusMap', {
            ...this.store.getValue().props.syllabusMap,
            [id]: syllabusId,
          });
        }
      }),
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.reference_question',
        action: 'fetch',
        context: {
          referenceQuestionId: id,
        },
      }),
    );
  }

  fetchReferenceQuestionStatus(id: ReferenceQuestionStoreModel['id']): Observable<string | null> {
    return this.httpService.fetchReferenceQuestionStatus(id).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.reference_question',
        action: 'fetch',
        context: {
          referenceQuestionId: id,
        },
      }),
    );
  }

  update(id: ReferenceQuestionStoreModel['id'], model: ReferenceQuestionUpdateModel) {
    const httpModel = this.mapUpdateModelToHttpUpdateModel(model);

    return this.httpService.update(id, httpModel).pipe(
      tap((data) => {
        this.store.upsert(id, this.mapReferenceQuestionHttpModelToStoreModel(data));
      }),
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.reference_question',
        action: 'update',
        context: {
          questionId: id,
          payload: model,
        },
      }),
    );
  }

  delete(id: ReferenceQuestionStoreModel['id']) {
    return this.confirmDialogService
      .confirm({
        title: 'Delete reference question',
        message: 'Are you sure you want to delete this reference question?',
        severity: 'warn',
        confirmColor: 'warn',
      })
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return EMPTY;
          }

          return this.httpService.delete(id).pipe(
            this.errorHandlerService.catchError(
              '[ReferenceQuestionHttpService]: Deleting reference question failed',
              rethrowError(),
              {
                toast: (err) => ({
                  title: 'An error occurred while deleting the reference question',
                  description: (err as Error)?.message,
                }),
                context: {
                  questionId: id,
                },
              },
            ),
            tap(() => {
              this.toastService.success('Reference question deleted');
              this.store.delete(id);
            }),
          );
        }),
      );
  }

  addToDataset(referenceQuestionId: ReferenceQuestionStoreModel['id'], datasetName: string) {
    return this.httpService.addToDataset(referenceQuestionId, datasetName).pipe(
      this.errorHandlerService.catchError('[ReferenceQuestionService]: Adding to dataset failed', rethrowError(), {
        toast: (err) => ({
          title: 'An error occurred while adding to Braintrust dataset',
          description: (err as Error)?.message,
        }),
        context: {
          referenceQuestionId: referenceQuestionId,
          datasetName,
        },
      }),
    );
  }

  getImageUrl(imageId: string) {
    return this.httpService.getPublicUrl(imageId);
  }

  getSignedUrlForImage(imageId: string) {
    return this.httpService.getSignedUrl(imageId);
  }

  async uploadImage(image: File, name?: string): Promise<string | null> {
    try {
      const path = await this.httpService.uploadImage(image, name);

      return path;
    } catch (err) {
      this.errorHandlerService.error('[ReferenceQuestionService]: Failed to upload the image', {
        toast: 'Failed to upload the image',
        err,
      });
      return null;
    }
  }

  removeImage(imageName: string) {
    return this.httpService.removeImage(imageName);
  }

  getAvailableSourceRefYears() {
    const minimumExamYear = 2021;

    return Array.from({ length: new Date().getFullYear() - minimumExamYear + 1 }, (_, i) => minimumExamYear + i);
  }

  createSeedQuestion(referenceQuestionId: ReferenceQuestionStoreModel['id']): Observable<number | null> {
    return this.httpService
      .createSeedQuestion(referenceQuestionId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.seed', action: 'create' }));
  }

  private mapCreateModelToHttpInsertModel(
    createModel: ReferenceQuestionCreateModel,
  ): TableInsertModel<'reference_questions'> {
    return sanitizeObject(createModel, REFERENCE_QUESTION_CREATE_ALLOWED_KEYS);
  }

  private mapUpdateModelToHttpUpdateModel(
    updateModel: ReferenceQuestionUpdateModel,
  ): TableUpdateModel<'reference_questions'> {
    return sanitizeObject(updateModel, REFERENCE_QUESTION_UPDATE_ALLOWED_KEYS);
  }

  private mapReferenceQuestionHttpModelToStoreModel(
    httpModel: ReferenceQuestionHttpModel,
  ): ReferenceQuestionStoreModel {
    return {
      ...httpModel,
      llm_feedback: httpModel.llm_feedback as ReferenceQuestionStoreModel['llm_feedback'],
      source_ref: httpModel.source_ref as ReferenceQuestionStoreModel['source_ref'],
      status: httpModel.status as ReferenceQuestionStoreModel['status'],
      parsed_markscheme: httpModel.parsed_markscheme as ReferenceQuestionStoreModel['parsed_markscheme'],
      parsed_markscheme_llm_feedback:
        httpModel.parsed_markscheme_llm_feedback as ReferenceQuestionStoreModel['parsed_markscheme_llm_feedback'],
      question_items: httpModel.question_items as unknown as QuestionItemV2[],
      metadata: httpModel.metadata as ReferenceQuestionStoreModel['metadata'],
    };
  }
}
